import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView as trackPageViewExternal } from "@/services/analytics";
import { useTracking } from "@/hooks/useInteractionTracking";
import { captureLastTouchFromUrl } from "@/lib/lastTouchAttribution";
import { capture as posthogCapture } from "@/services/posthog";
import { extractEventIdFromPath } from "@/lib/analytics";

export const usePageTracking = (): void => {
  const location = useLocation();
  const { trackPageView } = useTracking();
  
  // Use ref to avoid trackPageView in dependency array
  const trackPageViewRef = useRef(trackPageView);
  
  // Keep ref updated
  useEffect(() => {
    trackPageViewRef.current = trackPageView;
  }, [trackPageView]);

  useEffect(() => {
    try {
      // Capture last-touch attribution (UTMs / gclid / fbclid) for purchase
      // attribution. Survives SPA navigation via sessionStorage.
      captureLastTouchFromUrl();

      // Track to database as page_view with correct interaction_type
      trackPageViewRef.current(location.pathname, {
        search: location.search,
        referrer: document.referrer,
      });

      // Also track to Google Analytics and Meta Pixel
      trackPageViewExternal(location.pathname + location.search);

      // PostHog $pageview — skip admin routes (admin data is sensitive and
      // low value for product analytics). PostHog reads window.location itself
      // so don't override $current_url.
      if (!location.pathname.startsWith("/admin")) {
        const eventId = extractEventIdFromPath(location.pathname);
        posthogCapture("$pageview", eventId ? { event_id: eventId } : undefined);
      }
    } catch (error) {
      console.error("Failed to track page view:", error);
    }
  }, [location.pathname, location.search]);
};
